import { useQueryClient } from '@tanstack/react-query'
import {
  Form,
  InputNumber,
  Input,
  Switch,
  DatePicker,
  Select,
  Card,
  Flex,
  Button,
  Checkbox
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import dayjs, { Dayjs } from 'dayjs'
import { useState } from 'react'

import {
  useSettingsServiceGetSystemSettingsEndpoint,
  useSettingsServiceGetSystemSettingsEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  SettingsService,
  SystemSettingsGroupDto,
  SystemSettingsValueDto,
  SystemValueType,
  UpdateSystemSettingsValueRequest
} from 'src/api/requests'
import { LoadingScreen, notification } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Settings = {
  id: string
} & SystemSettingsValueDto

type onChangeHandler = (
  id: string,
  key: keyof Settings,
  value: string | number | boolean | string[] | number[] | boolean[] | null
) => void

const { RangePicker } = DatePicker

const fieldRenderers: Record<
  string,
  (setting: Settings, onChange: onChangeHandler) => JSX.Element
> = {
  [SystemValueType.INTEGER]: (setting, onChange) => (
    <InputNumber
      min={setting?.validationRules?.MinValue}
      max={setting?.validationRules?.MaxValue}
      defaultValue={setting?.n}
      onChange={(value) => onChange(setting?.id, 'n', value)}
    />
  ),
  [SystemValueType.DECIMAL]: (setting, onChange) => (
    <InputNumber
      step={0.01}
      defaultValue={setting?.f ?? undefined}
      onChange={(value) => onChange(setting?.id, 'f', value)}
    />
  ),
  [SystemValueType.BOOLEAN]: (setting, onChange) => (
    <Switch
      defaultChecked={setting?.b ?? false}
      onChange={(checked) => onChange(setting?.id, 'b', checked)}
    />
  ),
  [SystemValueType.STRING]: (setting, onChange) => (
    <Input
      defaultValue={setting?.s ?? ''}
      onChange={(e) => onChange(setting?.id, 's', e.target.value)}
    />
  ),
  [SystemValueType.DATE]: (setting, onChange) => (
    <DatePicker
      defaultValue={setting?.d ? dayjs(setting?.d) : null}
      onChange={(_, dateString) => onChange(setting?.id, 'd', dateString)}
    />
  ),
  [SystemValueType.DATE_TIME]: (setting, onChange) => (
    <DatePicker
      showTime
      defaultValue={setting?.dt ? dayjs(setting?.dt) : null}
      onChange={(_, dateString) => onChange(setting?.id, 'dt', dateString)}
    />
  ),
  [SystemValueType.STRING_LIST]: (setting, onChange) => (
    <Select
      mode="tags"
      defaultValue={setting?.sl}
      onChange={(values) => onChange(setting?.id, 'sl', values)}
    />
  ),
  [SystemValueType.INTEGER_LIST]: (setting, onChange) => (
    <Select
      mode="tags"
      defaultValue={setting?.nl?.map(String)}
      onChange={(values) => onChange(setting?.id, 'nl', values.map(Number))}
    />
  ),
  [SystemValueType.DECIMAL_LIST]: (setting, onChange) => (
    <Select
      mode="tags"
      defaultValue={setting?.fl?.map(String)}
      onChange={(values) => onChange(setting?.id, 'fl', values.map(Number))}
    />
  ),
  [SystemValueType.BOOLEAN_LIST]: (setting, onChange) => {
    const currentValues = setting?.bl ?? setting.bl ?? []

    const onCheckboxChange = (index: number, checked: boolean) => {
      const updatedValues = [...currentValues]
      updatedValues[index] = checked

      onChange(setting.id, 'bl', updatedValues)
    }

    return (
      <>
        {setting?.bl?.map((_, index) => (
          <Checkbox
            key={index}
            defaultChecked={currentValues[index] ?? false}
            onChange={(e) => onCheckboxChange(index, e.target.checked)}
          >
            Option {index + 1}
          </Checkbox>
        ))}
      </>
    )
  },
  [SystemValueType.DATE_LIST]: (setting, onChange) => (
    <RangePicker
      defaultValue={
        setting?.dl?.map(dayjs) as [Dayjs | undefined, Dayjs | undefined]
      }
      onChange={(_, dateStrings) => onChange(setting?.id, 'dl', dateStrings)}
    />
  ),
  [SystemValueType.DATE_TIME_LIST]: (setting, onChange) => (
    <RangePicker
      showTime
      defaultValue={
        setting?.dtl?.map(dayjs) as [Dayjs | undefined, Dayjs | undefined]
      }
      onChange={(_, dateStrings) => onChange(setting?.id, 'dtl', dateStrings)}
    />
  ),
  default: () => <Input placeholder="Unsupported type" disabled />
}

const renderField = (setting: Settings, onChange: onChangeHandler) =>
  fieldRenderers[setting?.valueType ?? 'default']?.(setting, onChange) ||
  fieldRenderers.default(setting, onChange)

const renderSettings = (
  currentLang: 'ro' | 'ru',
  onChange: onChangeHandler,
  settings?: SystemSettingsGroupDto[]
) => {
  if (!settings || settings?.length === 0) return null

  return settings.map((setting) => (
    <Form.Item
      label={setting.name?.[currentLang]}
      tooltip={setting.description?.[currentLang]}
      key={setting.id}
      valuePropName="checked"
      style={{
        marginBottom: 24
      }}
    >
      {renderField(setting as Settings, onChange)}
    </Form.Item>
  ))
}

const renderGroups = (
  currentLang: 'ro' | 'ru',
  onChange: onChangeHandler,
  groups?: SystemSettingsGroupDto[]
) => {
  if (!groups) return null

  return groups
    ?.filter(
      (group) =>
        (group.settings && group.settings.length > 0) ||
        (group.subGroups && group.subGroups.length > 0)
    )
    ?.map((group) => (
      <Card title={group.name?.[currentLang]} key={group.id}>
        {renderSettings(currentLang, onChange, group?.settings)}
        {(group?.subGroups?.length ?? 0) > 0 &&
          renderGroups(currentLang, onChange, group?.subGroups)}
      </Card>
    ))
}

export const SystemSettings = () => {
  const { t, currentLang } = useLocales()
  const queryClient = useQueryClient()

  const { data, isLoading } = useSettingsServiceGetSystemSettingsEndpoint()

  const [formValues, setFormValues] = useState<Record<string, AnyObject>>({})

  const onChange: onChangeHandler = (id, key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: { ...prev[id], [key]: value }
    }))
  }

  const onSave = async () => {
    try {
      await Promise.all(
        Object.entries(formValues).map(([id, values]) => {
          const requestBody: UpdateSystemSettingsValueRequest = {
            n: values.n ?? null,
            nl: values.nl ?? null,
            f: values.f ?? null,
            fl: values.fl ?? null,
            b: values.b ?? null,
            bl: values.bl ?? null,
            s: values.s ?? null,
            sl: values.sl ?? null,
            d: values.d ?? null,
            dl: values.dl ?? null,
            dt: values.dt ?? null,
            dtl: values.dtl ?? null,
            g: values.g ?? null,
            gl: values.gl ?? null
          }

          return SettingsService.updateSystemSettingsValueEndpoint(
            id,
            requestBody
          )
        })
      )
      queryClient.invalidateQueries({
        queryKey: [useSettingsServiceGetSystemSettingsEndpointKey]
      })
      notification.success({ message: t('common.save_success') })
    } catch (e) {
      const error = e as ApiError
      formErrorHandler(error.body.errors)
    }
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Card>
      <Form layout="vertical">
        {data?.systemSettings?.map((category) => (
          <Card
            title={category?.name?.[currentLang?.value]}
            key={category?.id}
            style={{
              marginBottom: 24
            }}
          >
            <Flex vertical gap={24}>
              {renderGroups(
                currentLang?.value,
                onChange,
                category?.subGroups || []
              )}
            </Flex>
          </Card>
        ))}

        <Button onClick={onSave} type="primary" htmlType="submit">
          {t('common.save')}
        </Button>
      </Form>
    </Card>
  )
}
